import type { Store } from "redux";
import firebase from "firebase/app";
import "firebase/firestore";

import firestore from "src/apiService/firestore";
import { getUserIdSync } from "src/config/storage";
import type { AppState } from "src/store";
import {
  addSalesRecords,
  updateSalesRecords,
  deleteSalesRecords,
  setOldestSaleYear,
} from "src/store/sale/actions";
import {
  addTransactionRecords,
  updateTransactionRecords,
  deleteTransactionRecords,
  setOldestTransactionYear,
} from "src/store/transaction/actions";
import { logOut } from "src/store/system/actions";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import {
  setSalesTransactionsFirestoreLoading,
  setSalesTransactionsInitialSyncComplete,
  setUnreviewedPresence,
} from "src/store/userDataLoad/actions";
import { calculatedLoadedFromDateSelector } from "src/store/userDataLoad/selector";
import { endLoadTiming, startLoadTiming } from "src/utils/loadTimings";
import {
  type Deferred,
  promiseWithResolvers,
} from "src/utils/promiseWithResolvers";
import { getDateTimePartsInTimeZone } from "src/utils/timezone";

/**
 * Firestore composite indexes required:
 * - Sales: user ==, max_date >=, max_date <
 * - Transactions: user ==, date >=, date <=
 * All sale documents should include populated `min_date` and `max_date` Timestamps (same four source fields; segment queries use `max_date`).
 */

export type ExtendManualLoadedFromDateParams = {
  userId: string;
  startDate?: Date | null;
  /** Runs after recording the wider window, before awaiting the date-segment sync. */
  onBeforeFirestoreWait?: () => void;
};

function mapDocChanges(
  snapshot: firebase.firestore.QuerySnapshot,
  adds: any[],
  changes: any[],
  deletes: any[]
) {
  snapshot.docChanges().forEach((change) => {
    const data = change.doc.data();
    if (data && !data.id) data.id = change.doc.id;
    if (change.type === "added") {
      adds.push(data);
    } else if (change.type === "modified") {
      changes.push(data);
    } else if (change.type === "removed") {
      deletes.push(data);
    }
  });
}

type QueryWhereClause = [
  field: string,
  op: firebase.firestore.WhereFilterOp,
  value: unknown
];

function formatWhereClausesForLog(whereClauses: QueryWhereClause[]): string {
  return whereClauses
    .map(([field, op, value]) => {
      if (value instanceof firebase.firestore.Timestamp) {
        return `${field} ${op} ${value.toDate().toISOString()}`;
      }
      return `${field} ${op} ${String(value)}`;
    })
    .join(" AND ");
}

export class DataLoaderManager {
  private readonly store: Store<AppState>;

  private activeUserId: string | null = null;

  private baseFrom: Date | null = null;

  private baseUnsubscribeSales: (() => void) | null = null;

  private baseUnsubscribeTransactions: (() => void) | null = null;

  private backfillUnsubscribers = new Map<
    string,
    { sales: () => void; transactions: () => void }
  >();

  private unreviewedSalesUnsubscribe: (() => void) | null = null;

  private unreviewedTransactionsUnsubscribe: (() => void) | null = null;

  /** Resolves when the lazy unreviewed sales/transactions first snapshots arrive. */
  private unreviewedSalesFirstSnapshot: Deferred | null = null;

  private unreviewedTransactionsFirstSnapshot: Deferred | null = null;

  private unreviewedSalesTimerActive = false;

  private unreviewedTransactionsTimerActive = false;

  /** Last user whose lightweight review-presence queries were run. */
  private unreviewedPresenceUserId: string | null = null;

  /** Optional poll for review notifications while the unbounded listeners are deferred. */
  private unreviewedPresenceRefreshTimer: number | null = null;

  private originalSaleUnsubscribers = new Map<string, () => void>();

  /**
   * Ref-count for in-flight Firestore “first snapshot” units (sales segment, tx segment,
   * unreviewed listeners, per-doc originals). Drives a single Redux loading flag.
   */
  private firestoreLoadingDepth = 0;

  /** Resolvers for in-flight date-segment sync promises (flushed on reset/dispose). */
  private readonly pendingSegmentSyncReleases = new Set<() => void>();

  private coreDataReady = false;

  private reduxAutoSyncUnsubscribe: (() => void) | null = null;

  /** DOM timers return `number`; avoid `NodeJS.Timeout` from Node typings. */
  private autoSyncDebounceId: number | null = null;

  /** Dedupes `store.subscribe` noise: only reschedule when uid or effective lower bound changes. */
  private lastObservedAutoSyncKey: string | null = null;

  /**
   * Report-driven expansion only (not persisted with filters). Merged with dashboard/txn filter
   * start the same way the old `effectiveLoadedFromDateSelector` did.
   */
  private manualLoadedFromIso: string | null = null;

  private oldestYearFetchUserId: string | null = null;

  constructor(store: Store<AppState>) {
    this.store = store;
  }

  /**
   * Per-user canary flag: skip the unbounded review listeners until a dialog asks for them.
   * Note: the date-bounded base query still includes unreviewed docs inside the window.
   */
  private shouldDeferUnreviewedLoad(): boolean {
    const user = this.store.getState().system.user;
    return user?.featureFlags?.deferUnreviewedLoad === true;
  }

  /**
   * Start the all-time unreviewed sales/returns listener on demand (review dialogs).
   * Idempotent: resolves after the first snapshot, or immediately if already loaded.
   */
  ensureUnreviewedSalesLoaded(): Promise<void> {
    const userId = this.activeUserId || getUserIdSync().trim();
    if (!userId) return Promise.resolve();
    // Dialog-triggered loads must never re-block the already-open shell.
    return this.startUnreviewedSalesListener(userId, {
      touchGlobalLoading: false,
    });
  }

  /**
   * Start the all-time reviewed === false transactions listener on demand
   * (categorize-expenses dialog).
   */
  ensureUnreviewedTransactionsLoaded(): Promise<void> {
    const userId = this.activeUserId || getUserIdSync().trim();
    if (!userId) return Promise.resolve();
    // Dialog-triggered loads must never re-block the already-open shell.
    return this.startUnreviewedTransactionsListener(userId, {
      touchGlobalLoading: false,
    });
  }

  private getEffectiveLoadedFromDate(state: AppState): Date {
    const calculated = calculatedLoadedFromDateSelector(state);
    if (!this.manualLoadedFromIso) return calculated;
    const manual = new Date(this.manualLoadedFromIso);
    if (isNaN(manual.getTime())) return calculated;
    return manual.getTime() < calculated.getTime() ? manual : calculated;
  }

  /**
   * When true, subscribes to the Redux store and keeps Firestore date windows aligned with
   * dashboard / transaction filters plus any in-memory manual report expansion.
   * When false, tears down that subscription and resets listeners.
   */
  setCoreDataReady(ready: boolean): void {
    this.coreDataReady = ready;
    if (!ready) {
      this.stopReduxAutoSync();
      this.resetWindow();
      return;
    }
    // Avoid a one-frame “shell open” gap: `handleReduxStoreAutoSync` debounces 50ms before
    // `syncSalesAndTransactionsWindow` sets `salesTransactionsFirestoreLoading`, while
    // persisted Redux can still have `initialSalesTransactionsSyncComplete === true`.
    if (!this.reduxAutoSyncUnsubscribe) {
      this.store.dispatch(setSalesTransactionsInitialSyncComplete(false));
    }
    this.ensureReduxAutoSyncSubscription();
  }

  private stopReduxAutoSync(): void {
    this.clearAutoSyncDebounce();
    this.lastObservedAutoSyncKey = null;
    if (this.reduxAutoSyncUnsubscribe) {
      this.reduxAutoSyncUnsubscribe();
      this.reduxAutoSyncUnsubscribe = null;
    }
  }

  private clearAutoSyncDebounce(): void {
    if (this.autoSyncDebounceId != null) {
      window.clearTimeout(this.autoSyncDebounceId);
      this.autoSyncDebounceId = null;
    }
  }

  private ensureReduxAutoSyncSubscription(): void {
    if (this.reduxAutoSyncUnsubscribe) return;
    this.reduxAutoSyncUnsubscribe = this.store.subscribe(() => {
      this.handleReduxStoreAutoSync();
    });
    this.handleReduxStoreAutoSync();
  }

  private handleReduxStoreAutoSync(): void {
    if (!this.coreDataReady) return;
    const uid = getUserIdSync().trim();
    if (!uid) {
      this.clearAutoSyncDebounce();
      // Tear down Firestore first (emulator/offline invalid token often leaves Redux
      // persisted `loggedIn` while `currentUser` is already null).
      this.resetWindow();
      if (this.store.getState().system.loggedIn) {
        this.store.dispatch(logOut());
      }
      return;
    }
    const from = this.getEffectiveLoadedFromDate(this.store.getState());
    const key = `${uid}|${from.getTime()}`;
    if (key === this.lastObservedAutoSyncKey) return;
    this.lastObservedAutoSyncKey = key;
    this.clearAutoSyncDebounce();
    this.autoSyncDebounceId = window.setTimeout(() => {
      this.autoSyncDebounceId = null;
      if (!this.coreDataReady) return;
      const userId = getUserIdSync().trim();
      if (!userId) return;
      const rangeFrom = this.getEffectiveLoadedFromDate(this.store.getState());
      void this.syncSalesAndTransactionsWindow(userId, rangeFrom);
    }, 50);
  }

  /**
   * Aligns date-bounded Sales/Transactions listeners with `rangeFrom` (earliest inclusive bound).
   * Resolves when any **new** date segment's first sales + first transactions snapshot have
   * been processed (or immediately when there is nothing to do). Unreviewed / per-doc
   * listeners are not awaited here.
   *
   * When `opts.touchGlobalLoading` is `false`, skips Redux `salesTransactionsFirestoreLoading`
   * (e.g. report manual backfill callers await the promise instead).
   */
  syncSalesAndTransactionsWindow(
    userId: string,
    rangeFrom: Date,
    opts?: { touchGlobalLoading?: boolean }
  ): Promise<void> {
    const touchGlobalLoading = opts?.touchGlobalLoading !== false;

    if (this.activeUserId && this.activeUserId !== userId) {
      this.resetWindow();
    }
    if (!this.activeUserId) {
      this.activeUserId = userId;
      this.fetchOldestDataYears(userId);
      this.store.dispatch(setSalesTransactionsInitialSyncComplete(false));
    }

    if (!this.baseFrom) {
      this.baseFrom = rangeFrom;
      const segmentPromise = this.startSegmentListeners(
        userId,
        rangeFrom,
        undefined,
        "base",
        touchGlobalLoading
      );
      if (this.shouldDeferUnreviewedLoad()) {
        // Do not block the shell on the all-time review queues. Keep only
        // cheap “is there anything to review?” flags for notifications.
        this.startUnreviewedPresencePolling(userId);
      } else {
        this.startUnreviewedSalesListener(userId).catch(() => {
          /* first-snapshot error is already logged by the listener */
        });
        this.startUnreviewedTransactionsListener(userId).catch(() => {
          /* first-snapshot error is already logged by the listener */
        });
      }
      return segmentPromise;
    }

    if (rangeFrom.getTime() >= this.baseFrom.getTime()) {
      return Promise.resolve();
    }

    const previousBase = this.baseFrom;
    this.baseFrom = rangeFrom;
    const key = `${rangeFrom.toISOString()}..${previousBase.toISOString()}`;
    if (this.backfillUnsubscribers.has(key)) {
      return Promise.resolve();
    }
    return this.startSegmentListeners(
      userId,
      rangeFrom,
      previousBase,
      key,
      touchGlobalLoading
    );
  }

  /**
   * If `startDate` is older than the effective loaded-from window, records manual expansion and
   * awaits the date-bounded Sales/Transactions segment sync (first snapshots).
   */
  async extendManualLoadedFromDate(
    params: ExtendManualLoadedFromDateParams
  ): Promise<void> {
    const { userId, startDate, onBeforeFirestoreWait } = params;
    const uid = userId.trim();
    if (!uid) return;

    if (!startDate || isNaN(startDate.getTime())) return;

    const state = this.store.getState();
    const calculated = calculatedLoadedFromDateSelector(state);
    const effective = this.getEffectiveLoadedFromDate(state);

    let candidateMs = startDate.getTime();
    if (this.manualLoadedFromIso) {
      const existing = new Date(this.manualLoadedFromIso);
      if (!isNaN(existing.getTime())) {
        candidateMs = Math.min(candidateMs, existing.getTime());
      }
    }
    const candidate = new Date(candidateMs);

    if (candidate.getTime() >= calculated.getTime()) return;
    if (candidate.getTime() >= effective.getTime()) return;

    this.manualLoadedFromIso = candidate.toISOString();

    onBeforeFirestoreWait?.();

    await this.syncSalesAndTransactionsWindow(uid, candidate, {
      touchGlobalLoading: false,
    });
    this.lastObservedAutoSyncKey = null;
    this.handleReduxStoreAutoSync();
  }

  private flushPendingSegmentSyncReleases(): void {
    const pending = [...this.pendingSegmentSyncReleases];
    this.pendingSegmentSyncReleases.clear();
    pending.forEach((release) => release());
  }

  private maybeMarkInitialComplete(): void {
    const s = this.store.getState().userDataLoad;
    if (s.initialSalesTransactionsSyncComplete) return;
    if (!s.salesTransactionsFirestoreLoading) {
      this.store.dispatch(setSalesTransactionsInitialSyncComplete(true));
    }
  }

  private markFirestoreLoading(inFlight: boolean): void {
    const delta = inFlight ? 1 : -1;
    const prev = this.firestoreLoadingDepth;
    this.firestoreLoadingDepth = Math.max(0, prev + delta);
    if (prev === 0 && this.firestoreLoadingDepth > 0) {
      this.store.dispatch(setSalesTransactionsFirestoreLoading(true));
    } else if (prev > 0 && this.firestoreLoadingDepth === 0) {
      this.store.dispatch(setSalesTransactionsFirestoreLoading(false));
      this.maybeMarkInitialComplete();
    }
  }

  private startSegmentListeners(
    userId: string,
    fromInclusive: Date,
    toExclusive: Date | undefined,
    segmentKey: string,
    touchGlobalLoading = true
  ): Promise<void> {
    const db = firestore();
    const dispatch = this.store.dispatch;
    const fromTs = firebase.firestore.Timestamp.fromDate(fromInclusive);
    const toTs = toExclusive
      ? firebase.firestore.Timestamp.fromDate(toExclusive)
      : undefined;
    const isBaseSegment = segmentKey === "base";
    const salesWhereClauses: QueryWhereClause[] = [["max_date", ">=", fromTs]];
    const transactionsWhereClauses: QueryWhereClause[] = [
      ["date", ">=", fromTs],
    ];
    if (toTs) {
      salesWhereClauses.push(["max_date", "<", toTs]);
      transactionsWhereClauses.push(["date", "<", toTs]);
    }

    if (touchGlobalLoading) {
      this.markFirestoreLoading(true);
      this.markFirestoreLoading(true);
    }
    let salesFirst = true;
    let transactionsFirst = true;

    const onFirstSalesSnapshotDone = () => {
      if (touchGlobalLoading) {
        this.markFirestoreLoading(false);
      }
    };

    const onFirstTransactionsSnapshotDone = () => {
      if (touchGlobalLoading) {
        this.markFirestoreLoading(false);
      }
    };

    let salesQuery = db
      .collection("Sales")
      .where("user", "==", userId)
      .where("max_date", ">=", fromTs);
    let transactionsQuery = db
      .collection("Transactions")
      .where("user", "==", userId)
      .where("date", ">=", fromTs);

    if (toTs) {
      salesQuery = salesQuery.where("max_date", "<", toTs);
      transactionsQuery = transactionsQuery.where("date", "<", toTs);
    }

    const salesFirstLoadTimerLabel = isBaseSegment
      ? `[DataLoaderManager] Sales first-load where: ${formatWhereClausesForLog(
          salesWhereClauses
        )}`
      : "";
    const transactionsFirstLoadTimerLabel = isBaseSegment
      ? `[DataLoaderManager] Transactions first-load where: ${formatWhereClausesForLog(
          transactionsWhereClauses
        )}`
      : "";
    if (isBaseSegment) {
      console.time(salesFirstLoadTimerLabel);
      console.time(transactionsFirstLoadTimerLabel);
      startLoadTiming("salesFirstLoad");
      startLoadTiming("transactionsFirstLoad");
    }

    const segmentPromise = new Promise<void>((resolve) => {
      let settled = false;
      const release = () => {
        if (settled) return;
        settled = true;
        this.pendingSegmentSyncReleases.delete(release);
        resolve();
      };
      this.pendingSegmentSyncReleases.add(release);

      let salesDone = false;
      let transactionsDone = false;
      const tryComplete = () => {
        if (salesDone && transactionsDone) {
          queueMicrotask(release);
        }
      };

      const unsubscribeSales = salesQuery.onSnapshot(
        (snapshot) => {
          const adds: any[] = [];
          const changes: any[] = [];
          const deletes: any[] = [];
          mapDocChanges(snapshot, adds, changes, deletes);
          if (adds.length) dispatch(addSalesRecords(adds));
          if (changes.length) dispatch(updateSalesRecords(changes));
          if (deletes.length) dispatch(deleteSalesRecords(deletes));
          if (salesFirst) {
            salesFirst = false;
            queueMicrotask(() => {
              if (isBaseSegment) {
                console.timeEnd(salesFirstLoadTimerLabel);
                endLoadTiming("salesFirstLoad");
              }
              onFirstSalesSnapshotDone();
              salesDone = true;
              tryComplete();
            });
          }
        },
        (error) => {
          console.error(`Sales ${segmentKey} snapshot error`, error);
          if (salesFirst) {
            salesFirst = false;
            queueMicrotask(() => {
              if (isBaseSegment) {
                console.timeEnd(salesFirstLoadTimerLabel);
                endLoadTiming("salesFirstLoad");
              }
              onFirstSalesSnapshotDone();
              salesDone = true;
              tryComplete();
            });
          }
        }
      );

      const unsubscribeTransactions = transactionsQuery.onSnapshot(
        (snapshot) => {
          const adds: any[] = [];
          const changes: any[] = [];
          const deletes: any[] = [];
          mapDocChanges(snapshot, adds, changes, deletes);
          if (adds.length) dispatch(addTransactionRecords(adds));
          if (changes.length) dispatch(updateTransactionRecords(changes));
          if (deletes.length) dispatch(deleteTransactionRecords(deletes));
          if (transactionsFirst) {
            transactionsFirst = false;
            queueMicrotask(() => {
              if (isBaseSegment) {
                console.timeEnd(transactionsFirstLoadTimerLabel);
                endLoadTiming("transactionsFirstLoad");
              }
              onFirstTransactionsSnapshotDone();
              transactionsDone = true;
              tryComplete();
            });
          }
        },
        (error) => {
          console.error(`Transactions ${segmentKey} snapshot error`, error);
          if (transactionsFirst) {
            transactionsFirst = false;
            queueMicrotask(() => {
              if (isBaseSegment) {
                console.timeEnd(transactionsFirstLoadTimerLabel);
                endLoadTiming("transactionsFirstLoad");
              }
              onFirstTransactionsSnapshotDone();
              transactionsDone = true;
              tryComplete();
            });
          }
        }
      );

      if (segmentKey === "base") {
        this.baseUnsubscribeSales = unsubscribeSales;
        this.baseUnsubscribeTransactions = unsubscribeTransactions;
      } else {
        this.backfillUnsubscribers.set(segmentKey, {
          sales: unsubscribeSales,
          transactions: unsubscribeTransactions,
        });
      }
    });

    return segmentPromise;
  }

  private parseYearFromUnknownDate(
    value: unknown,
    timeZone: string
  ): number | null {
    if (!value) return null;
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      return getDateTimePartsInTimeZone(value, timeZone).year;
    }
    if (value instanceof firebase.firestore.Timestamp) {
      const date = value.toDate();
      if (isNaN(date.getTime())) return null;
      return getDateTimePartsInTimeZone(date, timeZone).year;
    }
    return null;
  }

  private async fetchOldestDataYears(userId: string): Promise<void> {
    if (this.oldestYearFetchUserId === userId) return;
    const label = "[DataLoaderManager] fetchOldestDataYears";
    console.time(label);
    startLoadTiming("fetchOldestDataYears");
    this.oldestYearFetchUserId = userId;
    const timeZone = effectiveUserTimeZoneSelector(this.store.getState());
    const db = firestore();
    const [salesSnapshotResult, transactionsSnapshotResult] =
      await Promise.allSettled([
        db
          .collection("Sales")
          .where("user", "==", userId)
          .orderBy("sale_date", "asc")
          .limit(1)
          .get(),
        db
          .collection("Transactions")
          .where("user", "==", userId)
          .orderBy("date", "asc")
          .limit(1)
          .get(),
      ]);

    let oldestSaleYear: number | null = null;
    if (salesSnapshotResult.status === "fulfilled") {
      const firstSale = salesSnapshotResult.value.docs[0]?.data();
      oldestSaleYear = this.parseYearFromUnknownDate(
        firstSale?.sale_date,
        timeZone
      );
    } else {
      console.error(
        "Failed to fetch oldest sale year",
        salesSnapshotResult.reason
      );
    }

    let oldestTransactionYear: number | null = null;
    if (transactionsSnapshotResult.status === "fulfilled") {
      const firstTransaction = transactionsSnapshotResult.value.docs[0]?.data();
      oldestTransactionYear = this.parseYearFromUnknownDate(
        firstTransaction?.date,
        timeZone
      );
    } else {
      console.error(
        "Failed to fetch oldest transaction year",
        transactionsSnapshotResult.reason
      );
    }

    this.store.dispatch(setOldestSaleYear(oldestSaleYear));
    this.store.dispatch(setOldestTransactionYear(oldestTransactionYear));
    console.timeEnd(label);
    endLoadTiming("fetchOldestDataYears");
  }

  /**
   * Cheap existence checks used when the unbounded listeners are deferred.
   * Returns enables review notifications without loading the full queues.
   */
  private startUnreviewedPresencePolling(userId: string): void {
    this.stopUnreviewedPresencePolling();
    void this.refreshUnreviewedPresence(userId, true);
    // The old listeners were real-time. Keep notification flags reasonably
    // fresh without bringing the full queues back onto the critical path.
    this.unreviewedPresenceRefreshTimer = window.setInterval(() => {
      if (!this.coreDataReady) return;
      void this.refreshUnreviewedPresence(userId, true);
    }, 5 * 60 * 1000);
  }

  private stopUnreviewedPresencePolling(): void {
    if (this.unreviewedPresenceRefreshTimer == null) return;
    window.clearInterval(this.unreviewedPresenceRefreshTimer);
    this.unreviewedPresenceRefreshTimer = null;
  }

  private async refreshUnreviewedPresence(
    userId: string,
    force = false
  ): Promise<void> {
    if (!force && this.unreviewedPresenceUserId === userId) return;
    this.unreviewedPresenceUserId = userId;

    // An active listener already owns its category (and updates presence on
    // every snapshot); skip redundant reads for it.
    const checkSales = !this.unreviewedSalesUnsubscribe;
    const checkTransactions = !this.unreviewedTransactionsUnsubscribe;
    if (!checkSales && !checkTransactions) return;
    const db = firestore();
    const [anySales, returns, transactions] = await Promise.allSettled([
      checkSales
        ? db
            .collection("Sales")
            .where("user", "==", userId)
            .where("unreviewed", "==", true)
            .limit(1)
            .get()
        : Promise.resolve(null),
      checkSales
        ? db
            .collection("Sales")
            .where("user", "==", userId)
            .where("unreviewed", "==", true)
            .where("is_return", "==", true)
            .limit(1)
            .get()
        : Promise.resolve(null),
      checkTransactions
        ? db
            .collection("Transactions")
            .where("user", "==", userId)
            .where("reviewed", "==", false)
            .limit(1)
            .get()
        : Promise.resolve(null),
    ]);

    const patch: {
      sales?: boolean;
      returns?: boolean;
      transactions?: boolean;
    } = {};
    // Any unreviewed sale is enough for the generic “review sales” alert.
    // Returns are also sales, so this can be a false positive when the user
    // only has unreviewed returns; the returns alert is exact.
    if (checkSales && anySales.status === "fulfilled" && anySales.value) {
      patch.sales = !anySales.value.empty;
    }
    if (checkSales && returns.status === "fulfilled" && returns.value) {
      patch.returns = !returns.value.empty;
    }
    if (
      checkTransactions &&
      transactions.status === "fulfilled" &&
      transactions.value
    ) {
      patch.transactions = !transactions.value.empty;
    }
    this.store.dispatch(setUnreviewedPresence(patch));
  }

  teardownListeners(): void {
    this.baseUnsubscribeSales?.();
    this.baseUnsubscribeSales = null;
    this.baseUnsubscribeTransactions?.();
    this.baseUnsubscribeTransactions = null;

    this.unreviewedSalesUnsubscribe?.();
    this.unreviewedSalesUnsubscribe = null;
    this.unreviewedSalesFirstSnapshot?.resolve();
    this.unreviewedSalesFirstSnapshot = null;
    if (this.unreviewedSalesTimerActive) {
      console.timeEnd(
        "[DataLoaderManager] Sales first-load where: unreviewed == true"
      );
      endLoadTiming("unreviewedSalesFirstLoad");
      this.unreviewedSalesTimerActive = false;
    }

    this.unreviewedTransactionsUnsubscribe?.();
    this.unreviewedTransactionsUnsubscribe = null;
    this.unreviewedTransactionsFirstSnapshot?.resolve();
    this.unreviewedTransactionsFirstSnapshot = null;
    if (this.unreviewedTransactionsTimerActive) {
      console.timeEnd(
        "[DataLoaderManager] Transactions first-load where: reviewed == false"
      );
      endLoadTiming("unreviewedTransactionsFirstLoad");
      this.unreviewedTransactionsTimerActive = false;
    }

    this.unreviewedPresenceUserId = null;
    this.stopUnreviewedPresencePolling();

    for (const unsubscribe of this.originalSaleUnsubscribers.values()) {
      unsubscribe();
    }
    this.originalSaleUnsubscribers.clear();

    for (const { sales, transactions } of this.backfillUnsubscribers.values()) {
      sales();
      transactions();
    }
    this.backfillUnsubscribers.clear();
  }

  private startUnreviewedSalesListener(
    userId: string,
    opts?: { touchGlobalLoading?: boolean }
  ): Promise<void> {
    if (this.unreviewedSalesUnsubscribe) {
      return this.unreviewedSalesFirstSnapshot?.promise ?? Promise.resolve();
    }
    const touchGlobalLoading = opts?.touchGlobalLoading !== false;
    const db = firestore();
    const dispatch = this.store.dispatch;
    const timerLabel = `[DataLoaderManager] Sales first-load where: unreviewed == true`;
    if (touchGlobalLoading) this.markFirestoreLoading(true);
    let first = true;
    const deferred = promiseWithResolvers();
    this.unreviewedSalesFirstSnapshot = deferred;

    const settleFirst = (error?: unknown) => {
      if (!first) return;
      first = false;
      if (this.unreviewedSalesTimerActive) {
        console.timeEnd(timerLabel);
        endLoadTiming("unreviewedSalesFirstLoad");
        this.unreviewedSalesTimerActive = false;
      }
      if (touchGlobalLoading) this.markFirestoreLoading(false);
      if (this.unreviewedSalesFirstSnapshot === deferred) {
        this.unreviewedSalesFirstSnapshot = null;
      }
      if (error) deferred.reject(error);
      else deferred.resolve();
    };

    console.time(timerLabel);
    startLoadTiming("unreviewedSalesFirstLoad");
    this.unreviewedSalesTimerActive = true;
    try {
      this.unreviewedSalesUnsubscribe = db
        .collection("Sales")
        .where("user", "==", userId)
        .where("unreviewed", "==", true)
        .onSnapshot(
          { includeMetadataChanges: true },
          async (snapshot) => {
            const adds: any[] = [];
            const changes: any[] = [];
            const deletes: any[] = [];
            mapDocChanges(snapshot, adds, changes, deletes);
            if (adds.length) dispatch(addSalesRecords(adds));
            if (changes.length) dispatch(updateSalesRecords(changes));
            if (deletes.length) {
              const deletedWithId = deletes.filter((item) => !!item?.id);
              const deletedWithoutId = deletes.filter((item) => !item?.id);
              if (deletedWithoutId.length) {
                dispatch(deleteSalesRecords(deletedWithoutId));
              }

              if (deletedWithId.length) {
                const existence = await Promise.all(
                  deletedWithId.map(async (item) => {
                    try {
                      const snap = await db
                        .collection("Sales")
                        .doc(item.id)
                        .get();
                      return {
                        item,
                        exists: snap.exists,
                        data: snap.data(),
                      };
                    } catch (error) {
                      console.warn(
                        "[DataLoaderManager] Failed to verify removed unreviewed sale; treating as delete",
                        { saleId: item.id, error }
                      );
                      return { item, exists: false, data: undefined };
                    }
                  })
                );

                // Use the freshly-fetched server data (not the snapshot-cached
                // `item`) so we don't clobber Redux with stale field values when
                // a sale is updated such that it leaves the unreviewed query
                // (e.g. matching a sale to inventory writes a new
                // `purchase_price` along with `unreviewed: false`).
                const removedAsReviewed = existence
                  .filter(({ exists, data }) => exists && !!data)
                  .map(({ item, data }) => ({
                    ...(data as Record<string, unknown>),
                    id: item.id,
                    unreviewed: false,
                  }));
                const actuallyDeleted = existence
                  .filter(({ exists }) => !exists)
                  .map(({ item }) => item);

                if (removedAsReviewed.length) {
                  dispatch(updateSalesRecords(removedAsReviewed));
                }
                if (actuallyDeleted.length) {
                  dispatch(deleteSalesRecords(actuallyDeleted));
                }
              }
            }

            // Keep the lightweight notification flags in sync while the queue
            // is loaded (including after the last row is reviewed).
            let hasSales = false;
            let hasReturns = false;
            snapshot.docs.forEach((doc) => {
              const data: any = doc.data();
              if (!data) return;
              if (data.is_return || data.original_id) hasReturns = true;
              else hasSales = true;
            });
            dispatch(
              setUnreviewedPresence({ sales: hasSales, returns: hasReturns })
            );

            const originalSaleIds = new Set<string>();
            snapshot.docs.forEach((doc) => {
              const data: any = doc.data();
              if (!data) return;
              if ((data.is_return || data.original_id) && data.original_id) {
                originalSaleIds.add(data.original_id);
              }
            });
            this.syncOriginalSaleListeners(
              userId,
              originalSaleIds,
              touchGlobalLoading
            );

            // A cached snapshot may contain only the docs already in the
            // local cache (e.g. the current date window). Keep showing the
            // dialog loader until the server snapshot arrives with the full
            // review queue.
            if (!snapshot.metadata.fromCache) {
              settleFirst();
            }
          },
          (error) => {
            console.error("Unreviewed sales snapshot error", error);
            this.unreviewedSalesUnsubscribe?.();
            this.unreviewedSalesUnsubscribe = null;
            settleFirst(error);
          }
        );
    } catch (error) {
      settleFirst(error);
    }
    return deferred.promise;
  }

  private startUnreviewedTransactionsListener(
    userId: string,
    opts?: { touchGlobalLoading?: boolean }
  ): Promise<void> {
    if (this.unreviewedTransactionsUnsubscribe) {
      return (
        this.unreviewedTransactionsFirstSnapshot?.promise ?? Promise.resolve()
      );
    }
    const touchGlobalLoading = opts?.touchGlobalLoading !== false;
    const db = firestore();
    const dispatch = this.store.dispatch;
    const timerLabel =
      "[DataLoaderManager] Transactions first-load where: reviewed == false";
    if (touchGlobalLoading) this.markFirestoreLoading(true);
    let first = true;
    const deferred = promiseWithResolvers();
    this.unreviewedTransactionsFirstSnapshot = deferred;

    const settleFirst = (error?: unknown) => {
      if (!first) return;
      first = false;
      if (this.unreviewedTransactionsTimerActive) {
        console.timeEnd(timerLabel);
        endLoadTiming("unreviewedTransactionsFirstLoad");
        this.unreviewedTransactionsTimerActive = false;
      }
      if (touchGlobalLoading) this.markFirestoreLoading(false);
      if (this.unreviewedTransactionsFirstSnapshot === deferred) {
        this.unreviewedTransactionsFirstSnapshot = null;
      }
      if (error) deferred.reject(error);
      else deferred.resolve();
    };

    console.time(timerLabel);
    startLoadTiming("unreviewedTransactionsFirstLoad");
    this.unreviewedTransactionsTimerActive = true;
    try {
      this.unreviewedTransactionsUnsubscribe = db
        .collection("Transactions")
        .where("user", "==", userId)
        .where("reviewed", "==", false)
        .onSnapshot(
          { includeMetadataChanges: true },
          async (snapshot) => {
            const adds: any[] = [];
            const changes: any[] = [];
            const deletes: any[] = [];
            mapDocChanges(snapshot, adds, changes, deletes);
            if (adds.length) dispatch(addTransactionRecords(adds));
            if (changes.length) dispatch(updateTransactionRecords(changes));
            if (deletes.length) {
              const deletedWithId = deletes.filter((item) => !!item?.id);
              const deletedWithoutId = deletes.filter((item) => !item?.id);
              if (deletedWithoutId.length) {
                dispatch(deleteTransactionRecords(deletedWithoutId));
              }

              if (deletedWithId.length) {
                const existence = await Promise.all(
                  deletedWithId.map(async (item) => {
                    try {
                      const snap = await db
                        .collection("Transactions")
                        .doc(item.id)
                        .get();
                      return {
                        item,
                        exists: snap.exists,
                        data: snap.data(),
                      };
                    } catch (error) {
                      console.warn(
                        "[DataLoaderManager] Failed to verify removed unreviewed transaction; treating as delete",
                        { transactionId: item.id, error }
                      );
                      return { item, exists: false, data: undefined };
                    }
                  })
                );

                // Use the freshly-fetched server data (not the snapshot-cached
                // `item`) so we don't clobber Redux with stale field values when
                // a transaction is updated such that it leaves the unreviewed
                // query (e.g. an edit that also flips `reviewed` to true).
                const removedAsReviewed = existence
                  .filter(({ exists, data }) => exists && !!data)
                  .map(({ item, data }) => ({
                    ...(data as Record<string, unknown>),
                    id: item.id,
                    reviewed: true,
                  }));
                const actuallyDeleted = existence
                  .filter(({ exists }) => !exists)
                  .map(({ item }) => item);

                if (removedAsReviewed.length) {
                  dispatch(updateTransactionRecords(removedAsReviewed));
                }
                if (actuallyDeleted.length) {
                  dispatch(deleteTransactionRecords(actuallyDeleted));
                }
              }
            }

            dispatch(
              setUnreviewedPresence({ transactions: snapshot.size > 0 })
            );
            // See the sales listener: cached snapshots can be partial.
            if (!snapshot.metadata.fromCache) {
              settleFirst();
            }
          },
          (error) => {
            console.error("Unreviewed transactions snapshot error", error);
            this.unreviewedTransactionsUnsubscribe?.();
            this.unreviewedTransactionsUnsubscribe = null;
            settleFirst(error);
          }
        );
    } catch (error) {
      settleFirst(error);
    }
    return deferred.promise;
  }

  private syncOriginalSaleListeners(
    userId: string,
    ids: Set<string>,
    touchGlobalLoading = true
  ): void {
    for (const [id, unsubscribe] of this.originalSaleUnsubscribers.entries()) {
      if (!ids.has(id)) {
        unsubscribe();
        this.originalSaleUnsubscribers.delete(id);
      }
    }

    const dispatch = this.store.dispatch;
    const db = firestore();

    for (const id of ids) {
      if (this.originalSaleUnsubscribers.has(id)) continue;
      if (touchGlobalLoading) this.markFirestoreLoading(true);
      let first = true;
      const unsubscribe = db
        .collection("Sales")
        .doc(id)
        .onSnapshot(
          (doc) => {
            if (doc.exists) {
              const data: any = doc.data();
              if (data?.user === userId) {
                if (!data.id) data.id = doc.id;
                dispatch(addSalesRecords([data]));
              }
            } else {
              dispatch(deleteSalesRecords([{ id }]));
            }
            if (first) {
              first = false;
              if (touchGlobalLoading) this.markFirestoreLoading(false);
            }
          },
          (error) => {
            console.error(`Original sale '${id}' snapshot error`, error);
            if (first) {
              first = false;
              if (touchGlobalLoading) this.markFirestoreLoading(false);
            }
          }
        );
      this.originalSaleUnsubscribers.set(id, unsubscribe);
    }
  }

  resetWindow(): void {
    this.lastObservedAutoSyncKey = null;
    this.clearAutoSyncDebounce();
    this.manualLoadedFromIso = null;
    this.oldestYearFetchUserId = null;
    this.flushPendingSegmentSyncReleases();
    this.teardownListeners();
    this.store.dispatch(setOldestSaleYear(null));
    this.store.dispatch(setOldestTransactionYear(null));
    this.store.dispatch(
      setUnreviewedPresence({
        sales: false,
        returns: false,
        transactions: false,
      })
    );
    this.baseFrom = null;
    this.activeUserId = null;
    this.firestoreLoadingDepth = 0;
    const udl = this.store.getState().userDataLoad;
    if (udl.initialSalesTransactionsSyncComplete) {
      this.store.dispatch(setSalesTransactionsInitialSyncComplete(false));
    }
    if (udl.salesTransactionsFirestoreLoading) {
      this.store.dispatch(setSalesTransactionsFirestoreLoading(false));
    }
  }

  dispose(): void {
    this.setCoreDataReady(false);
  }
}
